import { format, parseISO } from 'date-fns'
import { Calendar, Clock, Loader2, Video, X } from 'lucide-react'
import { useState } from 'react'
import api from 'services/api'
import { clearMask, formatCpf } from 'utils/formatters'

type FindAppointmentModalProps = {
  onClose: () => void
}

type FoundAppointment = {
  date: string
  hour: string
  event_link: string | null
  status: string
  prescriber_name: string | null
}

const STATUS_LABELS: Record<string, string> = {
  paid: 'Confirmada',
  waiting_appointment: 'Confirmada',
  waiting_payment: 'Aguardando pagamento',
  payment_refused: 'Pagamento recusado'
}

/**
 * "Já tenho uma consulta" (marcas com hasAppointmentLookup, ex. Vikura):
 * busca a última consulta do paciente por email + CPF na rota anônima
 * /scheduling/appointment/last-by-patient e mostra data, horário e o link
 * da videochamada. A resposta do backend é mínima (sem PII do paciente) e o
 * erro é genérico por design — não revela se o e-mail existe na base.
 */
export default function FindAppointmentModal({ onClose }: FindAppointmentModalProps) {
  const [email, setEmail] = useState('')
  const [cpf, setCpf] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [appointment, setAppointment] = useState<FoundAppointment | null>(null)

  const handleSearch = async () => {
    const cleanEmail = email.trim().toLowerCase()
    const cleanCpf = clearMask(cpf)
    if (!cleanEmail || cleanCpf.length !== 11) {
      setError('Informe o e-mail e o CPF usados no agendamento.')
      return
    }

    setLoading(true)
    setError(null)
    try {
      const response = await api.post('/scheduling/appointment/last-by-patient', {
        email: cleanEmail,
        cpf: cleanCpf
      })
      setAppointment(response.data.appointment)
    } catch {
      setError('Consulta não encontrada. Confira o e-mail e o CPF informados.')
    } finally {
      setLoading(false)
    }
  }

  const statusLabel = appointment ? STATUS_LABELS[appointment.status] : undefined

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      {/* Card branco de propósito: o theme css das marcas pinta qualquer
          bg-primary-* com a cor cheia. */}
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <h2 className="font-spartan text-xl font-bold text-gray-800">Minha consulta</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="border-none bg-transparent p-1 text-gray-400 hover:text-gray-600">
            <X className="size-5" />
          </button>
        </div>

        {!appointment ? (
          <>
            <p className="mb-4 text-sm text-gray-500">
              Informe o e-mail e o CPF usados no agendamento para ver o horário e o link da sua consulta.
            </p>

            <div className="flex flex-col gap-3">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="E-mail"
                className="w-full rounded-lg border border-solid border-gray-200 p-3 text-sm text-gray-700 focus:border-primary-700 focus:outline-none"
              />
              <input
                type="text"
                inputMode="numeric"
                value={cpf}
                maxLength={14}
                onChange={(event) => setCpf(formatCpf(event.target.value))}
                placeholder="CPF"
                className="w-full rounded-lg border border-solid border-gray-200 p-3 text-sm text-gray-700 focus:border-primary-700 focus:outline-none"
              />

              {error && <p className="text-sm text-red-500">{error}</p>}

              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg border-none bg-primary-700 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-800 disabled:opacity-60">
                {loading && <Loader2 className="size-4 animate-spin" />}
                Buscar consulta
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl bg-gray-50 p-4">
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar className="size-4 shrink-0 text-primary-700" />
                  <span className="font-medium">{format(parseISO(appointment.date), 'dd/MM/yyyy')}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="size-4 shrink-0 text-primary-700" />
                  <span className="font-medium">{appointment.hour}</span>
                </div>
                {appointment.prescriber_name && (
                  <p className="text-gray-500">
                    Médico: <span className="font-medium text-gray-700">{appointment.prescriber_name}</span>
                  </p>
                )}
                {statusLabel && (
                  <p className="text-gray-500">
                    Status: <span className="font-medium text-gray-700">{statusLabel}</span>
                  </p>
                )}
              </div>
            </div>

            {appointment.event_link ? (
              <a
                href={appointment.event_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 rounded-lg border-none bg-primary-700 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-800">
                <Video className="size-4" />
                Acessar consulta
              </a>
            ) : (
              <p className="text-center text-sm text-gray-500">
                O link da videochamada ainda não está disponível. Ele é liberado perto do horário da consulta.
              </p>
            )}

            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-lg border border-solid border-gray-200 bg-white py-3 text-sm font-semibold text-gray-600 shadow-sm hover:border-gray-400">
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
